import { LottieComponentProps } from 'lottie-react';

import { LoadingAnimations } from '../enum/LoadingAnimation';

/** `lottie-react` exports no nominal type for this, so it is derived from the prop it feeds. */
export type AnimationData = LottieComponentProps['animationData'];

type AnimationLoader = () => Promise<AnimationData>;

function load(importer: () => Promise<{ default: AnimationData }>): AnimationLoader {
  // A dynamic import resolves to the module, not to its default export.
  return () => importer().then((module) => module.default);
}

/**
 * One dynamic import per animation, listed explicitly.
 *
 * The previous version re-exported all 24 files from a barrel and indexed it with a runtime key, so
 * webpack could not drop any of them: 567 KB of JSON was inlined into the entry chunk for a tenant
 * that displays exactly one. Explicit importers rather than a template literal, because a variable
 * import would build a webpack context whose contents are far harder to reason about.
 *
 * Note there are 25 enum values for 24 files: `DEFAULT` and `COFFEE` are the same animation.
 */
const animationLoaders: Record<LoadingAnimations, AnimationLoader> = {
  [LoadingAnimations.ASIAN_RICE]: load(() => import('../assets/loaders/asian-rice.json')),
  [LoadingAnimations.BEER]: load(() => import('../assets/loaders/beer.json')),
  [LoadingAnimations.BREAD]: load(() => import('../assets/loaders/bread.json')),
  [LoadingAnimations.BUILD]: load(() => import('../assets/loaders/build.json')),
  [LoadingAnimations.CAKE]: load(() => import('../assets/loaders/cake.json')),
  [LoadingAnimations.CEREAL]: load(() => import('../assets/loaders/cereal.json')),
  [LoadingAnimations.CROISSANT]: load(() => import('../assets/loaders/croissant.json')),
  [LoadingAnimations.COFFEE]: load(() => import('../assets/loaders/coffee.json')),
  [LoadingAnimations.DONUT]: load(() => import('../assets/loaders/donut.json')),
  [LoadingAnimations.DISH]: load(() => import('../assets/loaders/dish.json')),
  [LoadingAnimations.DRINK]: load(() => import('../assets/loaders/drink.json')),
  [LoadingAnimations.FISH_AND_CHIPS]: load(() => import('../assets/loaders/fish-and-chips.json')),
  [LoadingAnimations.FRENCH_FRIES]: load(() => import('../assets/loaders/french-fries.json')),
  [LoadingAnimations.FRIED_PASTRY]: load(() => import('../assets/loaders/fried-pastry.json')),
  [LoadingAnimations.HAMBURGUER]: load(() => import('../assets/loaders/hamburger.json')),
  [LoadingAnimations.ICE_CREAM]: load(() => import('../assets/loaders/ice-cream.json')),
  [LoadingAnimations.PANCAKE]: load(() => import('../assets/loaders/pancake.json')),
  [LoadingAnimations.PIZZA]: load(() => import('../assets/loaders/pizza.json')),
  [LoadingAnimations.POPCORN]: load(() => import('../assets/loaders/popcorn.json')),
  [LoadingAnimations.SALAD]: load(() => import('../assets/loaders/salad.json')),
  [LoadingAnimations.SUSHI]: load(() => import('../assets/loaders/sushi.json')),
  [LoadingAnimations.TACOS]: load(() => import('../assets/loaders/tacos.json')),
  [LoadingAnimations.TURKEY]: load(() => import('../assets/loaders/turkey.json')),
  [LoadingAnimations.WINE]: load(() => import('../assets/loaders/wine.json')),
  [LoadingAnimations.DEFAULT]: load(() => import('../assets/loaders/coffee.json')),
};

/**
 * Fetches one animation.
 *
 * The fallback is not defensive dressing: the value comes from the API and can be a legacy or
 * misspelled name that no longer matches the enum.
 */
export const getAnimation = (type: LoadingAnimations): Promise<AnimationData> =>
  (animationLoaders[type] ?? animationLoaders[LoadingAnimations.DEFAULT])();

export default getAnimation;
